import { glitchOnce, motionAllowedForGlitch } from './glitch';

/**
 * Random hologram glitches. On every page load a few KINDS of UI element are picked at
 * random (a different handful each refresh) and from then on, now and then, one random
 * visible element of each picked kind glitches for a moment. Elements are looked up fresh
 * at each tick, so it keeps working as screens re-render their `innerHTML`.
 */

export interface GlitchKind {
  id: string;
  selector: string;
}

export const GLITCH_KINDS: GlitchKind[] = [
  { id: 'title', selector: '.holo-title' },
  { id: 'balance', selector: '#shell-deltav' },
  { id: 'tab-icons', selector: '.shell-tab-btn [data-icon]' },
  { id: 'headings', selector: '#shell-content h1.font-heading, #shell-content h2.font-heading' },
  { id: 'panels', selector: '#shell-content .glass-panel' },
  { id: 'rail', selector: '.holo-rail > button' },
  { id: 'plates', selector: '#shell-content .card-plate, #shell-content .mars-horizon.rounded-lg' },
  { id: 'chips', selector: '.holo-chip' },
  { id: 'buttons', selector: '#shell-content button.bg-gold' },
  { id: 'minimap', selector: '#worldmap-minimap-canvas' },
  { id: 'tab-plates', selector: '.shell-tab-btn' },
  { id: 'hud-plates', selector: '#worldmap-focus-home, #chat-widget-pill, html[data-active-tab="worldmap"] .holo-bar > *' },
];

/** The landing page has no shell, so it brings its own list: marketing headings, hero art, cards, buttons. */
export const LANDING_GLITCH_KINDS: GlitchKind[] = [
  { id: 'hero-title', selector: '.ldg-hero-copy h1' },
  { id: 'headings', selector: '.landing-heading, .ldg-rule-title, .ldg-dossier-name' },
  { id: 'cta', selector: '.ldg-btn-primary' },
  { id: 'cards', selector: '.ldg-dossier, .ldg-bay, .ldg-waypoint, .ldg-holo-row' },
  { id: 'roster', selector: '.ldg-roster-btn' },
  { id: 'header', selector: '.ldg-header-row, .ldg-nav a' },
];

/** How many kinds are live in a session. */
export const KINDS_PER_SESSION = 4;
const MIN_GAP_MS = 3500;
const MAX_GAP_MS = 11000;

/** Pure: `count` distinct kinds chosen with `rand` (Fisher-Yates, partial). */
export function pickKinds(rand: () => number, kinds: GlitchKind[] = GLITCH_KINDS, count = KINDS_PER_SESSION): GlitchKind[] {
  const pool = kinds.slice();
  const n = Math.min(count, pool.length);
  for (let i = 0; i < n; i++) {
    const j = i + Math.floor(rand() * (pool.length - i));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return pool.slice(0, n);
}

function isShown(el: Element): boolean {
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0 && r.bottom > 0 && r.top < window.innerHeight;
}

/** Starts the random glitching. Returns the chosen kind ids (for debugging/tests) and a teardown. */
export function mountRandomGlitch(
  rand: () => number = Math.random,
  kinds: GlitchKind[] = GLITCH_KINDS,
): { kinds: string[]; teardown: () => void } {
  const chosen = pickKinds(rand, kinds);
  const timeouts = new Set<number>();
  let stopped = false;

  function schedule(kind: GlitchKind): void {
    const gap = MIN_GAP_MS + rand() * (MAX_GAP_MS - MIN_GAP_MS);
    const id = window.setTimeout(() => {
      timeouts.delete(id);
      if (stopped) return;
      fire(kind);
      schedule(kind);
    }, gap);
    timeouts.add(id);
  }

  function fire(kind: GlitchKind): void {
    if (document.hidden || !motionAllowedForGlitch()) return;
    const candidates = Array.from(document.querySelectorAll(kind.selector)).filter(isShown);
    if (candidates.length === 0) return;
    const el = candidates[Math.floor(rand() * candidates.length)];
    glitchOnce(el, 'soft');
    // Often a glitch stutters: a second, shorter burst right behind the first.
    if (rand() < 0.35) {
      const again = window.setTimeout(() => {
        timeouts.delete(again);
        if (!stopped && el.isConnected) glitchOnce(el, 'soft');
      }, 260);
      timeouts.add(again);
    }
  }

  for (const kind of chosen) schedule(kind);
  const ids = chosen.map((k) => k.id);
  document.documentElement.dataset.glitchKinds = ids.join(' ');

  return {
    kinds: ids,
    teardown(): void {
      stopped = true;
      for (const t of timeouts) window.clearTimeout(t);
      timeouts.clear();
      delete document.documentElement.dataset.glitchKinds;
    },
  };
}
